import { store } from '@/store';
import { BASE_URL } from '@/utils/constants/apiEndpoints';

import { interpretationRulePatchBody } from '../physician/TransmissionReportPage/ruleBasedTemplates';

import type { ReportInterpretationTemplate } from '../physician/TransmissionReportPage/ruleBasedTemplates';

export type InterpretationKind = 'clinician' | 'physician';

export interface InterpretationTemplateWrite {
  name: string;
  body: string;
  isDefault: boolean;
}

export interface InterpretationRuleRecord {
  id: string;
  kind: InterpretationKind;
  clinicId: string;
  ownerUserId?: string | null;
  rank: number;
  isCatchAll: boolean;
  isArchived?: boolean;
  deviceTypes: string[];
  alertTypes: string[];
  appointmentTypes: string[];
  templates: Array<{
    id: string;
    name: string;
    body: string;
    isDefault: boolean;
  }>;
}

export interface ClinicUserOption {
  id: string;
  given?: string;
  family?: string;
  email?: string;
}

export const DEVICE_TYPE_OPTIONS = ['ILR', 'PM', 'ICD', 'CRT-P', 'CRT-D'].map((value) => ({
  value,
  label: value
}));

export const ALERT_TYPE_OPTIONS = [
  { value: 'RED', label: 'Red' },
  { value: 'YELLOW', label: 'Yellow' },
  { value: 'ERI_TRACKING', label: 'ERI Tracking' },
  { value: 'NO_ALERT', label: 'No Alert' }
];

export const APPOINTMENT_TYPE_OPTIONS = [
  { value: 'ROUTINE', label: 'Routine' },
  { value: 'HEART_FAILURE', label: 'Heart Failure' },
  { value: 'COMBO', label: 'Combo' },
  { value: 'NON_ROUTINE', label: 'Non-Routine' },
  { value: 'ERI_TRACKING', label: 'ERI Tracking' },
  { value: 'PHYSICIAN_REQUEST', label: 'Physician Request' },
  { value: 'IN_CLINIC', label: 'In-Clinic' }
];

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = store.getState().auth.token;
  const response = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...init?.headers
    }
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      message?: string | string[];
    } | null;
    const message = Array.isArray(body?.message) ? body?.message.join(', ') : body?.message;
    throw new Error(message || `Request failed (${response.status})`);
  }
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

function items(data: InterpretationRuleRecord[] | { items?: InterpretationRuleRecord[] }) {
  if (Array.isArray(data)) return data;
  return data.items ?? [];
}

export async function listInterpretationRules(
  clinicId: string,
  kind: InterpretationKind,
  scope: { mine: true } | { ownerUserId?: string }
): Promise<InterpretationRuleRecord[]> {
  if ('mine' in scope) {
    const data = await apiFetch<
      InterpretationRuleRecord[] | { items?: InterpretationRuleRecord[] }
    >(`/organizations/${clinicId}/my/interpretation-rules?kind=${kind}`);
    return items(data);
  }
  const params = new URLSearchParams({ kind, includeArchived: 'true' });
  if (scope.ownerUserId) params.set('ownerUserId', scope.ownerUserId);
  const data = await apiFetch<InterpretationRuleRecord[] | { items?: InterpretationRuleRecord[] }>(
    `/organizations/${clinicId}/interpretation-rules?${params.toString()}`
  );
  return items(data);
}

export async function createInterpretationRule(
  clinicId: string,
  body: {
    kind: InterpretationKind;
    isCatchAll?: boolean;
    deviceTypes: string[];
    alertTypes: string[];
    appointmentTypes: string[];
    templates: InterpretationTemplateWrite[];
    ownerUserId?: string;
    mine?: boolean;
  }
): Promise<InterpretationRuleRecord> {
  const path = body.mine
    ? `/organizations/${clinicId}/my/interpretation-rules`
    : `/organizations/${clinicId}/interpretation-rules`;
  return apiFetch<InterpretationRuleRecord>(path, {
    method: 'POST',
    body: JSON.stringify({
      kind: body.kind,
      isCatchAll: body.isCatchAll === true,
      templates: body.templates,
      ...(body.ownerUserId ? { ownerUserId: body.ownerUserId } : {}),
      ...(body.isCatchAll
        ? {}
        : {
            deviceTypes: body.deviceTypes,
            alertTypes: body.alertTypes,
            appointmentTypes: body.appointmentTypes
          })
    })
  });
}

export async function updateInterpretationRule(
  clinicId: string,
  ruleId: string,
  body: {
    isCatchAll?: boolean;
    deviceTypes: string[];
    alertTypes: string[];
    appointmentTypes: string[];
    templates: InterpretationTemplateWrite[];
    mine?: boolean;
  }
): Promise<InterpretationRuleRecord> {
  const path = body.mine
    ? `/organizations/${clinicId}/my/interpretation-rules/${ruleId}`
    : `/organizations/${clinicId}/interpretation-rules/${ruleId}`;
  return apiFetch(path, {
    method: 'PATCH',
    body: JSON.stringify(
      interpretationRulePatchBody({
        isCatchAll: body.isCatchAll === true,
        deviceTypes: body.deviceTypes,
        alertTypes: body.alertTypes,
        appointmentTypes: body.appointmentTypes,
        templates: body.templates
      })
    )
  });
}

export async function listReportInterpretationTemplates(
  reportId: number,
  kind: InterpretationKind
): Promise<{ preselectedTemplateId?: string; templates: ReportInterpretationTemplate[] }> {
  return apiFetch(`/transmission-reports/${reportId}/interpretation-templates?kind=${kind}`);
}

export async function applyReportInterpretationTemplate(
  reportId: number,
  templateId: string,
  kind: InterpretationKind
): Promise<{ text: string; templateId: string }> {
  return apiFetch(
    `/transmission-reports/${reportId}/interpretation-templates/${templateId}/apply?kind=${kind}`,
    { method: 'POST' }
  );
}

export async function archiveInterpretationRule(
  clinicId: string,
  ruleId: string,
  mine?: boolean
): Promise<void> {
  const path = mine
    ? `/organizations/${clinicId}/my/interpretation-rules/${ruleId}/archive`
    : `/organizations/${clinicId}/interpretation-rules/${ruleId}/archive`;
  await apiFetch(path, { method: 'POST' });
}

export async function restoreInterpretationRule(
  clinicId: string,
  ruleId: string
): Promise<InterpretationRuleRecord> {
  return apiFetch(`/organizations/${clinicId}/interpretation-rules/${ruleId}/restore`, {
    method: 'POST'
  });
}

export async function reorderInterpretationRules(
  clinicId: string,
  kind: InterpretationKind,
  ruleIds: string[],
  scope: { mine?: boolean; ownerUserId?: string }
): Promise<void> {
  const path = scope.mine
    ? `/organizations/${clinicId}/my/interpretation-rules/reorder`
    : `/organizations/${clinicId}/interpretation-rules/reorder`;
  await apiFetch(path, {
    method: 'POST',
    body: JSON.stringify({
      kind,
      ruleIds,
      ...(scope.ownerUserId ? { ownerUserId: scope.ownerUserId } : {})
    })
  });
}

export async function listClinicUsers(clinicId: string): Promise<ClinicUserOption[]> {
  const data = await apiFetch<{ data?: ClinicUserOption[]; users?: ClinicUserOption[] }>(
    '/users/paginated',
    {
      method: 'POST',
      body: JSON.stringify({
        organizationIds: [clinicId],
        page: 1,
        limit: 200,
        sortBy: 'given',
        sortDirection: 'ASC'
      })
    }
  );
  return data.data ?? data.users ?? [];
}
